/* eslint-disable no-underscore-dangle */
import Request from '../common/api/request';
import ExhibitorMapper from '../common/exhibitingOrganisation/ExhibitorMapper';
import ProductMapper from '../common/exhibitingOrganisation/ProductMapper';
import ServiceResultFactory from '../common/api/ServiceResultFactory';
import SessionMapper from '../common/exhibitingOrganisation/SessionMapper';
import DocumentService from '../common/api/DocumentService';
import ProductsAndServicesService from '../filter/api/ProductsAndServicesService';
import FiltersMapper from '../common/exhibitingOrganisation/FiltersMapper';
import ExhibitingOrganisationService from '../filter/api/ExhibitingOrganisationService';

const Devices = {
  desktop: 'desktop',
  mobile: 'mobile'
};

const getSignedAssetForMultipleAssertKeys = (baseApiUrl, assetKeys, locale) => {
  let photoKeyList = '';
  assetKeys.forEach((assetKey) => {
    photoKeyList += `"${assetKey.replace(
      '/photo/',
      `/photo/${Devices.desktop}/`
    )}",
      "${assetKey.replace('/photo/', `/photo/${Devices.mobile}/`)}",`;
  });

  const query = `{
    signedAssets(keys: [${photoKeyList}])
    {
      items { url, key }
    }
  }`;
  return Request.graphqlRequest(baseApiUrl, query)
    .then(ServiceResultFactory.fromSuccess)
    .then(response => response.body.data.signedAssets.items)
    .catch(result => ServiceResultFactory.fromError(locale, result, false));
};

const getParticipantsWithPhotoUrls = async (
  participantData,
  baseApiUrl,
  interfaceLocale
) => {
  if (
    !participantData ||
    !participantData.items ||
    participantData.items.length === 0
  ) {
    return null;
  }
  const photoKey = [];
  /* eslint-disable no-param-reassign */
  participantData.items.forEach((data) => {
    data.desktopUrl = '';
    data.mobileUrl = '';
    if (data.photoKey) {
      photoKey.push(data.photoKey);
    }
  });
  if (photoKey.length === 0) {
    return participantData;
  }
  return getSignedAssetForMultipleAssertKeys(
    baseApiUrl,
    photoKey,
    interfaceLocale
  ).then((response) => {
    response.forEach((res) => {
      participantData.items.forEach((par) => {
        if (res.key.includes(`${par.participantId}/photo/${Devices.desktop}`)) {
          par.desktopUrl = res.url;
        }
        if (res.key.includes(`${par.participantId}/photo/${Devices.mobile}`)) {
          par.mobileUrl = res.url;
        }
      });
    });
    return ServiceResultFactory.fromSuccess(participantData);
  });
};

const getEventEditionDetails = (baseApiUrl, eventEditionId) => {
  const query = `{
    eventEdition(eventEditionId: "${eventEditionId}"){
      id
      businessUnitGbsCode
      isParticipationEvent
      supportedLocales
      isAtlas20Show
      isAnonymousRegistrationEnabled
    }
  }`;

  return Request.graphqlRequest(getGraphQLUrl(baseApiUrl), query);
}

const getGraphQLV2Url = baseApiUrl => `${baseApiUrl}v2/graphql`;

const getSessions = (
  baseApiUrl,
  exhibitingOrganisationIdFromDetails,
) => {
  const url = getGraphQLV2Url(baseApiUrl);
  const query = sessionQuery(exhibitingOrganisationIdFromDetails);

  return Request.graphqlRequest(url, query)
  .then(response => getResponse(response))
  .catch(() => {
    return [];
  });
};

const getResponse = (response) => {
   if (response !== undefined && response.body !== undefined && response.body.errors !== undefined && response.body.errors.length > 0) {
    return [];
  }

  return response.body.data.allExhibitorSessions || [];
}

const sessionQuery = (exhibitingOrganisationId) => `
{
  allExhibitorSessions(exhibitingOrganisationId: "${exhibitingOrganisationId}") {  
    title
    location
    description
    start
    end
    speakers {
      name
    }
  }
}
`;

const getEventEditionExtraCharacteristicsRequest = (baseUrl, eventEditionId) =>
  Request.get(`${baseUrl}v1/event-editions/${eventEditionId}/extra-characteristics`);

const extractAdditionalAddresses = (exhibitingOrganisation, interfaceLocale) => {
  if (!exhibitingOrganisation
    || !exhibitingOrganisation.multilingual) {
    return [];
  }
  const multilingual = exhibitingOrganisation.multilingual.find(x => x.locale.toLowerCase() === interfaceLocale.toLowerCase());
  return multilingual ? multilingual.additionalAddresses : [];
};

const mapResponsesToExhibitorDetails = (
  [
    filterResponse,
    documentsResponse,
    sessionResponse,
    // eslint-disable-next-line no-unused-vars
    eventEditionRequest,
    exhibitingOrganisationRingboxResponse
  ],
  interfaceLocale,
  primaryLocale
) => {

  const filterGroup = filterResponse
    ? FiltersMapper.mapFromProductsAndServicesResponse(
      filterResponse,
      interfaceLocale,
      primaryLocale
    )
    : null;
  const exhibitorDetails = ExhibitorMapper.fromDetailsResponse(
      exhibitingOrganisationRingboxResponse,
    filterGroup,
    interfaceLocale,
    primaryLocale
  );

  if (exhibitorDetails !== undefined) {
    exhibitorDetails.additionalAddresses = extractAdditionalAddresses(exhibitingOrganisationRingboxResponse, interfaceLocale);
  }

  const documents = documentsResponse;
  const exhibitingOrganisationResponse = (exhibitingOrganisationRingboxResponse !== undefined) ? exhibitingOrganisationRingboxResponse : {};
  const productResponse = { products: (exhibitingOrganisationResponse.products !== undefined) ? exhibitingOrganisationResponse.products : [] };
  const { products } = ProductMapper.fromResponse(
    productResponse,
    interfaceLocale,
    primaryLocale
  );

  return {
    exhibitorDetails,
    filterGroup,
    documents,
    products,
    sessions: SessionMapper.getSessions(sessionResponse || []),
    categories: exhibitingOrganisationRingboxResponse.categories,
    exhibitingProducers: exhibitingOrganisationRingboxResponse.exhibitingProducers,
    representedProducers: exhibitingOrganisationRingboxResponse.representedProducers,
    directors: exhibitingOrganisationRingboxResponse.directors,
    mainStandHolder: exhibitingOrganisationRingboxResponse.mainStandHolder,
    sharers: exhibitingOrganisationRingboxResponse.sharers,
    eventEdition: { ...eventEditionRequest.body.data.eventEdition },
  };
};

const getExhibitorDetails = (baseApiUrl, context, includeUnpublishedProducts) => {
  const {
    primaryLocale,
    interfaceLocale,
    eventEditionId,
    exhibitingOrganisationId
  } = context;

  const getExhibitingOrganisationRequest = () => ExhibitingOrganisationService.exhibitingOrganisation(
    baseApiUrl,
    eventEditionId,
    exhibitingOrganisationId,
    interfaceLocale, includeUnpublishedProducts
  );

  return getExhibitingOrganisationRequest()
  .then((exhibitingOrgResponse) => {

    const exhibitingOrganisationIdFromDetails = exhibitingOrgResponse.id;

    return Promise.all([
      ProductsAndServicesService.getProductsAndServices(
          baseApiUrl,
          eventEditionId,
          interfaceLocale
        ),
        DocumentService.getPublicDocuments(
          baseApiUrl,
          exhibitingOrganisationId,
          eventEditionId
        ),
        getSessions(
          baseApiUrl,
          exhibitingOrganisationIdFromDetails,
          interfaceLocale,
        ),
        getEventEditionDetails(baseApiUrl, eventEditionId),
        Promise.resolve(exhibitingOrgResponse),
        getEventEditionExtraCharacteristicsRequest(baseApiUrl,eventEditionId)
    ])
  })
    .then((responses) => {
      if (!responses[3].body.data.eventEdition.supportedLocales.includes(interfaceLocale)) {
        return Promise.reject(Error('invalid interface locale'));
      }
      responses[4].featuresTranslated = [];
            responses[5].body.extraCharacteristics.forEach((extraCharacteristic) => {
              extraCharacteristic.multilingual.forEach((multi) => {
                if (responses[4]?.extraCharacteristics.find(e => e.id === extraCharacteristic.id)
                    && multi.locale.toLowerCase() === interfaceLocale.toLowerCase()) {
                  responses[4]?.featuresTranslated.push(multi.name);
                }
              });
            });
      return mapResponsesToExhibitorDetails(
        responses,
        interfaceLocale,
        primaryLocale
      );
    })
    .then(ServiceResultFactory.fromSuccess)
    .catch(result =>
      ServiceResultFactory.fromError(interfaceLocale, result, false));
};

const getParticipations = ({
  baseApiUrl,
  exhibitingOrganisationId,
  interfaceLocale
}) => {
  const query = `{
    companyParticipations(exhibitingOrganisationId:"${exhibitingOrganisationId}") {
      items{ 
              id,
              participantId,
              firstName,
              lastName,
              photoKey,
              exhibitingOrganisation{
                companyName
              }
            }
      }
  }`;

  const url = `${baseApiUrl}graphql/`;
  return Request.graphqlRequest(url, query)
    .then(response => response.body.data.companyParticipations)
    .then(participantData =>
      getParticipantsWithPhotoUrls(participantData, url, interfaceLocale))
    .catch(result =>
      ServiceResultFactory.fromError(interfaceLocale, result, false));
};

const getGraphQLUrl = baseUrl => `${baseUrl}graphql/`;

const constructQuery = (exhibitingOrganisationId, includeUnpublishedProducts = true) =>
  `{
      exhibitingOrganisation(exhibitingOrganisationId:"${exhibitingOrganisationId}"){
        organisationId
        eventEditionId
        companyName
        packageId
        multilingual{
          displayName
          locale
        }        
        products(includeUnpublished:${includeUnpublishedProducts}){
          isPublished
          extraCharacteristics
          {
            id,
            multilingualItems
            {
              locale
              name
            }
            
          }
          id
          documents{
            id
            type
            url
            displayName
            size
            publishDate
          }
          exhibitingOrganisationId
          imageUrl
          isInnovative
          isNew
          videoId
          video{
            id
            defaultThumbnailUrl
            customThumbnailUrl
            partnerVideoUuid
            status
          }
          producerDetails {
            id
            firstName
            lastName
          }
          multilingual {
            title
            description
            locale
          }
          filterAttributes{
            id
            questionText{
              locale
              name
            }
            textualResponses{
              locale
              textResponse
            }
            responses
            {
              answerId
              parentId
              answerText{
                locale
                name
              }
            }
            sortOrder
          }
          attributes {
            id
            questionText{
              locale
              name
            }
            responses{
              answerId
              answerText{
                locale
                name
              }
            }
           textualResponses{
            locale
            textResponse
          }
            sortOrder
          } 
        }
      }
    }`;

const fetchExhibitorDetails = (
  baseApiUrl,
  exhibitingOrganisationId,
  interfaceLocale,
  includeUnpublishedProducts
) => {
  const query = constructQuery(exhibitingOrganisationId,includeUnpublishedProducts);

  const url = getGraphQLUrl(baseApiUrl);
  return Request.publicGraphqlRequest(url, query)
    .then(response => ServiceResultFactory.fromGraphqlSuccess(response, interfaceLocale, 'exhibitingOrganisation'));
};


export default {
  getExhibitorDetails, getParticipations, fetchExhibitorDetails
};
